/**
 * 用户偏好设置工具类
 * 统一管理用户在应用中的各种偏好设置（如币种选择、语言、主题等）
 */

// 币种类型
type CurrencyType = 'USDT' | 'USDC' | 'USD1'

// 偏好设置的 Key 常量
const PREFERENCE_KEYS = {
  // 兑换页面相关
  EXCHANGE_FROM: 'exchange_from_currency',
  EXCHANGE_TO: 'exchange_to_currency',
  // 汇款页面相关
  REMITTANCE_FROM: 'remittance_from_currency',
  REMITTANCE_TO: 'remittance_to_currency',
  // 可以添加更多偏好设置
  // LANGUAGE: 'user_language',
  // THEME: 'user_theme',
} as const

class UserPreferences {
  /**
   * 从 localStorage 获取项
   * @param key 存储键
   * @param defaultValue 默认值
   * @returns 存储的值或默认值
   */
  private static getItem<T>(key: string, defaultValue: T): T {
    try {
      const value = localStorage.getItem(key)
      if (!value) return defaultValue
      
      // 尝试解析 JSON，如果失败则返回原始值
      try {
        return JSON.parse(value) as T
      } catch {
        return value as T
      }
    } catch (error) {
      console.warn(`获取偏好设置失败 [${key}]:`, error)
      return defaultValue
    }
  }

  /**
   * 保存项到 localStorage
   * @param key 存储键
   * @param value 要存储的值
   */
  private static setItem<T>(key: string, value: T): void {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
      localStorage.setItem(key, stringValue)
    } catch (error) {
      console.error(`保存偏好设置失败 [${key}]:`, error)
    }
  }

  /**
   * 从 localStorage 移除项
   * @param key 存储键
   */
  private static removeItem(key: string): void {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error(`删除偏好设置失败 [${key}]:`, error)
    }
  }

  // ==================== Exchange 兑换相关 ====================
  
  /**
   * 获取兑换页面的"从"币种
   */
  static getExchangeFromCurrency(): CurrencyType {
    return this.getItem<CurrencyType>(PREFERENCE_KEYS.EXCHANGE_FROM, 'USDT')
  }

  /**
   * 设置兑换页面的"从"币种
   */
  static setExchangeFromCurrency(currency: CurrencyType): void {
    this.setItem(PREFERENCE_KEYS.EXCHANGE_FROM, currency)
  }

  /**
   * 获取兑换页面的"到"币种
   */
  static getExchangeToCurrency(): CurrencyType {
    return this.getItem<CurrencyType>(PREFERENCE_KEYS.EXCHANGE_TO, 'USDC')
  }

  /**
   * 设置兑换页面的"到"币种
   */
  static setExchangeToCurrency(currency: CurrencyType): void {
    this.setItem(PREFERENCE_KEYS.EXCHANGE_TO, currency)
  }

  // ==================== Remittance 汇款相关 ====================
  
  /**
   * 获取汇款页面的"从"币种
   */
  static getRemittanceFromCurrency(): CurrencyType {
    return this.getItem<CurrencyType>(PREFERENCE_KEYS.REMITTANCE_FROM, 'USDT')
  }

  /**
   * 设置汇款页面的"从"币种
   */
  static setRemittanceFromCurrency(currency: CurrencyType): void {
    this.setItem(PREFERENCE_KEYS.REMITTANCE_FROM, currency)
  }

  /**
   * 获取汇款页面的"到"币种
   */
  static getRemittanceToCurrency(): CurrencyType {
    return this.getItem<CurrencyType>(PREFERENCE_KEYS.REMITTANCE_TO, 'USDC')
  }

  /**
   * 设置汇款页面的"到"币种
   */
  static setRemittanceToCurrency(currency: CurrencyType): void {
    this.setItem(PREFERENCE_KEYS.REMITTANCE_TO, currency)
  }

  // ==================== 批量操作 ====================
  
  /**
   * 清除所有偏好设置
   */
  static clearAll(): void {
    Object.values(PREFERENCE_KEYS).forEach(key => {
      this.removeItem(key)
    })
  }

  /**
   * 清除兑换相关的偏好设置
   */
  static clearExchangePreferences(): void {
    this.removeItem(PREFERENCE_KEYS.EXCHANGE_FROM)
    this.removeItem(PREFERENCE_KEYS.EXCHANGE_TO)
  }

  /**
   * 清除汇款相关的偏好设置
   */
  static clearRemittancePreferences(): void {
    this.removeItem(PREFERENCE_KEYS.REMITTANCE_FROM)
    this.removeItem(PREFERENCE_KEYS.REMITTANCE_TO)
  }
}

export default UserPreferences
export type { CurrencyType }
